/**
 * InvestigatePanel — slide-over that fires an Investigate Why session
 * against a metric and streams the multi-agent timeline live via SSE.
 *
 * Any surface (KPI card, dashboard widget, insight, palette) can open
 * this panel by passing a `target` describing the metric + filters +
 * window. Phase 2 wires KPI Monitor cards; the same component will
 * power the other surfaces without changes.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Sparkles, Loader2, X, StopCircle, ArrowRight, CheckCircle2, AlertTriangle,
  Sigma, Layers, Clock, Send, Zap, MessageSquare, Share2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip as RechartTip,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

const CATEGORY_TINT = {
  temporal: 'bg-sky-500/15 text-sky-300 border-sky-500/25',
  mix: 'bg-purple-500/15 text-purple-300 border-purple-500/25',
  rate_volume: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/25',
  geography: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  channel: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  cohort: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/25',
  data_quality: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
  outliers: 'bg-orange-500/15 text-orange-300 border-orange-500/25',
  concentration: 'bg-red-500/15 text-red-300 border-red-500/25',
  retention: 'bg-teal-500/15 text-teal-300 border-teal-500/25',
};

function suggestQuestion(target) {
  // An explicit question (e.g. prefilled from an AI Insight) always wins.
  if (target?.question) return String(target.question);
  if (!target?.metric) return '';
  const dir = target?.direction || (target?.change_pct && target.change_pct < 0 ? 'drop' : 'change');
  const window = target?.window || 'the last period';
  return `Why did ${String(target.metric).replace(/_/g, ' ')} ${dir} in ${window}?`;
}

function EvidenceChart({ spec }) {
  if (!spec || !spec.rows || spec.rows.length === 0) return null;
  const rows = spec.rows.slice(0, 20);
  const y = spec.y;
  const x = spec.x;
  if (spec.type === 'line') {
    return (
      <div className="h-16 mt-2" data-testid="evidence-chart-line">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <XAxis dataKey={x} hide />
            <YAxis hide />
            <RechartTip contentStyle={{ background: 'rgba(9,9,11,0.9)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 10, borderRadius: 8 }} />
            <Line type="monotone" dataKey={y} stroke="#818cf8" strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return (
    <div className="h-16 mt-2" data-testid="evidence-chart-bar">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <XAxis dataKey={x} hide />
          <YAxis hide />
          <RechartTip contentStyle={{ background: 'rgba(9,9,11,0.9)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 10, borderRadius: 8 }} />
          <Bar dataKey={y} fill="#818cf8" radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function InvestigatePanel({ open, onOpenChange, target }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);
  const [investigation, setInvestigation] = useState(null);
  const [events, setEvents] = useState([]);   // ordered agent timeline
  const [verdict, setVerdict] = useState(null);
  const [status, setStatus] = useState('idle'); // idle|running|completed|failed|cancelled
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentAnchor, setCommentAnchor] = useState({ type: 'verdict', id: 'verdict' });
  const [modelName, setModelName] = useState('');
  const esRef = useRef(null);

  // Resolve the model's display name from its id (header shows the name, not the id).
  useEffect(() => {
    let alive = true;
    const mid = target?.model_id;
    if (!open || !mid) { setModelName(''); return undefined; }
    if (target?.model_name) { setModelName(target.model_name); return undefined; }
    api.getSemanticModels()
      .then((models) => {
        if (!alive) return;
        const m = (models || []).find((x) => (x.model_id || x.id) === mid);
        setModelName(m ? (m.name || m.model_name || mid) : mid);
      })
      .catch(() => { if (alive) setModelName(mid); });
    return () => { alive = false; };
  }, [open, target?.model_id, target?.model_name]);

  useEffect(() => {
    if (open) {
      setQuestion(suggestQuestion(target));
      setInvestigation(null);
      setEvents([]);
      setVerdict(null);
      setStatus('idle');
      // Hydrate immediately when opened via Replay so the comments UI
      // and existing verdict/timeline appear without waiting for a new
      // run.
      const replayId = target?.replay_investigation_id;
      if (replayId) {
        (async () => {
          try {
            setInvestigation({ investigation_id: replayId });
            setStatus('running');
            if (esRef.current) { esRef.current.close(); esRef.current = null; }
            startStream(replayId);
          } catch { /* ignore */ }
        })();
      } else if (target?.autoStart) {
        // One-click "Investigate this" from AI Explore — start immediately.
        const q0 = suggestQuestion(target);
        setQuestion(q0);
        if (q0.trim()) handleStart(q0);
      }
    } else if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, [open, target]);

  useEffect(() => () => {
    // Global cleanup on unmount
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
  }, []);

  const startStream = (investigationId) => {
    const url = api.investigationStreamUrl(investigationId);
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    const push = (event_type, payload) => {
      setEvents((prev) => [...prev, { event_type, payload, ts: new Date().toISOString() }]);
    };
    ['status', 'agent_start', 'agent_end', 'hypothesis', 'finding', 'error', 'clarifications_needed', 'clarifications_answered', 'critique'].forEach((type) => {
      es.addEventListener(type, (ev) => {
        try { const d = JSON.parse(ev.data || '{}'); push(type, d.payload); }
        catch { push(type, {}); }
      });
    });
    es.addEventListener('verdict', (ev) => {
      try { const d = JSON.parse(ev.data || '{}'); setVerdict(d.payload); }
      catch { /* ignore */ }
    });
    es.addEventListener('__end__', () => { setRunning(false); es.close(); esRef.current = null; });
    es.onerror = () => { /* let __end__ close, but stop the spinner */ setRunning(false); };
  };

  const handleStart = async (qOverride) => {
    const q = (typeof qOverride === 'string' ? qOverride : question).trim();
    if (!q) { toast.error('Enter a question first'); return; }
    setRunning(true);
    setStatus('running');
    setEvents([]);
    setVerdict(null);
    try {
      const inv = await api.startInvestigation({
        question: q,
        target: target || {},
        source: target?.source || 'palette',
      });
      setInvestigation(inv);
      startStream(inv.investigation_id);
    } catch (err) {
      setRunning(false);
      setStatus('failed');
      toast.error(err.message || 'Failed to start investigation');
    }
  };

  const handleCancel = async () => {
    if (!investigation) return;
    try { await api.cancelInvestigation(investigation.investigation_id); }
    catch { /* ignore */ }
    setStatus('cancelled');
    setRunning(false);
  };

  // Derive a compact status label from the last "status" event.
  useEffect(() => {
    const last = [...events].reverse().find((e) => e.event_type === 'status');
    if (last?.payload?.status) setStatus(last.payload.status);
  }, [events]);

  // Pending clarifications: latest `clarifications_needed` event whose
  // corresponding `clarifications_answered` hasn't happened yet.
  const pendingClarifications = (() => {
    const last = [...events].reverse().find((e) => e.event_type === 'clarifications_needed');
    if (!last) return null;
    const answered = events.some((e) => e.event_type === 'clarifications_answered');
    return answered ? null : (last.payload?.questions || []);
  })();
  const [clarifDraft, setClarifDraft] = useState({});

  const submitClarifications = async () => {
    if (!investigation) return;
    const answers = (pendingClarifications || []).map((q) => ({
      question: q,
      answer: (clarifDraft[q] || '').trim(),
    }));
    try {
      setRunning(true);
      await api.answerClarifications(investigation.investigation_id, answers);
      // Re-open the SSE stream — the previous one closed on the __end__
      // that the backend pushed when it paused at awaiting_input. The
      // backend replays every persisted event from the beginning, so
      // clear local state to avoid duplicates.
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      setEvents([]);
      setVerdict(null);
      startStream(investigation.investigation_id);
    } catch (err) {
      setRunning(false);
      toast.error(err.message || 'Failed to send answers');
    }
  };

  // Dedupe by hypothesis_id / finding_id so an SSE replay overlapping
  // with live events (e.g. reconnect after clarifications resume, or
  // rapid re-renders) doesn't render the same block twice.
  const hypotheses = (() => {
    const seen = new Set();
    return events
      .filter((e) => e.event_type === 'hypothesis')
      .map((e) => e.payload || {})
      .filter((h) => {
        const key = h.hypothesis_id || JSON.stringify(h.statement || '');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  })();

  const findings = (() => {
    const seen = new Set();
    return events
      .filter((e) => e.event_type === 'finding')
      .map((e) => e.payload || {})
      .filter((f) => {
        const key = f.finding_id || JSON.stringify(f.narrative || '');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  })();

  // ─── Comments ─────────────────────────────────────────────────
  const refreshComments = async () => {
    if (!investigation?.investigation_id) return;
    try {
      const r = await api.listInvestigationComments(investigation.investigation_id);
      setComments(r.comments || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (open && investigation?.investigation_id) refreshComments();
  }, [open, investigation?.investigation_id, verdict, refreshComments]);

  const submitComment = async () => {
    if (!investigation?.investigation_id || !commentDraft.trim()) return;
    try {
      await api.createInvestigationComment(investigation.investigation_id, {
        anchor_type: commentAnchor.type,
        anchor_id: commentAnchor.id,
        body: commentDraft.trim(),
      });
      setCommentDraft('');
      await refreshComments();
    } catch (err) {
      toast.error(err.message || 'Failed to post comment');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0" data-testid="investigate-panel"
        onPointerDownOutside={(e) => { const t = e.detail?.originalEvent?.target || e.target; if (t?.closest?.('[data-testid="floating-discussion"]')) e.preventDefault(); }}
        onInteractOutside={(e) => { const t = e.detail?.originalEvent?.target || e.target; if (t?.closest?.('[data-testid="floating-discussion"]')) e.preventDefault(); }}
      >
        <SheetHeader className="px-4 py-3 border-b border-border/40 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            Investigate Why
          </SheetTitle>
          {target?.metric && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Sigma className="w-3 h-3" />
              <span className="font-mono">{target.metric}</span>
              {target.window && <><span>·</span><Clock className="w-3 h-3" /><span>{target.window}</span></>}
              {target.model_id && <><span>·</span><Layers className="w-3 h-3" /><span data-testid="investigate-model-name">{modelName || target.model_id}</span></>}
            </div>
          )}
        </SheetHeader>

        <div className="p-4 border-b border-border/30 flex-shrink-0" data-testid="investigate-question-form">
          {(target?.insight_title || target?.insight_summary) && (
            <div className="mb-3 rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-2.5" data-testid="investigate-insight-context">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-indigo-300/90 mb-1">
                <Sparkles className="w-3 h-3" /> Investigating this insight
              </div>
              {target.insight_title && <p className="text-xs font-semibold text-foreground leading-snug">{target.insight_title}</p>}
              {target.insight_summary && <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed line-clamp-3">{target.insight_summary}</p>}
            </div>
          )}
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What do you want to know?"
            className="min-h-[64px] text-sm resize-none"
            disabled={running}
            data-testid="investigate-question-input"
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            {running ? (
              <Button variant="outline" size="sm" onClick={handleCancel} className="gap-1" data-testid="investigate-cancel">
                <StopCircle className="w-3.5 h-3.5" /> Cancel
              </Button>
            ) : (
              <Button size="sm" onClick={handleStart} disabled={!question.trim()} className="gap-1" data-testid="investigate-start">
                <Send className="w-3.5 h-3.5" /> Investigate
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 flex flex-col gap-3" data-testid="investigate-timeline">
            {events.length === 0 && !running && (
              <div className="text-center py-10 text-xs text-muted-foreground">
                Pose a question above and hit <span className="font-semibold text-foreground">Investigate</span>.
                The Planner and Synthesis agents will stream findings back here.
              </div>
            )}

            {/* Single live status line — collapses the agent timeline into one
                updating row instead of a long growing list. */}
            {(() => {
              const agentEvents = events.filter((e) => e.event_type === 'agent_start' || e.event_type === 'agent_end');
              if (agentEvents.length === 0) return null;
              const last = agentEvents[agentEvents.length - 1];
              const thinking = last.event_type === 'agent_start';
              const label = (last.payload?.agent || 'investigation').replace(/^investigation\./, '');
              return (
                <div className="flex items-center gap-2 text-[11px] rounded-lg border border-border/40 bg-card/30 px-3 py-2" data-testid="investigate-status-line">
                  {thinking ? <Loader2 className="w-3 h-3 animate-spin text-indigo-400" /> : <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                  <span className="font-mono text-foreground/90">{label}</span>
                  <span className={thinking ? 'italic text-muted-foreground' : 'text-emerald-300'}>
                    {thinking ? 'is running…' : `done${last.payload?.count !== undefined ? ` · ${last.payload.count} items` : ''}`}
                  </span>
                </div>
              );
            })()}

            {events.map((e, idx) => {
              // Dedupe hypothesis/finding blocks — the SSE endpoint replays
              // every persisted event on reconnect, which can duplicate
              // blocks visually. Skip the render if we've already shown
              // this id earlier in the timeline.
              if (e.event_type === 'hypothesis' && e.payload?.hypothesis_id) {
                const first = events.findIndex((x) => x.event_type === 'hypothesis' && x.payload?.hypothesis_id === e.payload.hypothesis_id);
                if (first !== idx) return null;
              }
              if (e.event_type === 'finding' && e.payload?.finding_id) {
                const first = events.findIndex((x) => x.event_type === 'finding' && x.payload?.finding_id === e.payload.finding_id);
                if (first !== idx) return null;
              }
              // Agent start/end no longer render as individual rows — the single
              // status line above reflects the current phase.
              if (e.event_type === 'agent_start' || e.event_type === 'agent_end') {
                return null;
              }
              if (e.event_type === 'hypothesis') {
                const h = e.payload || {};
                return (
                  <div key={idx} className="rounded-lg border border-border/40 bg-card/30 p-3" data-testid={`hypothesis-${h.hypothesis_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[9px] border ${CATEGORY_TINT[h.category] || 'bg-muted text-muted-foreground'}`}>{h.category || 'other'}</Badge>
                      {typeof h.prior_confidence === 'number' && (
                        <span className="text-[10px] text-muted-foreground">confidence {(h.prior_confidence * 100).toFixed(0)}%</span>
                      )}
                    </div>
                    <p className="text-xs text-foreground/90 leading-relaxed">{h.statement}</p>
                  </div>
                );
              }
              if (e.event_type === 'finding') {
                const f = e.payload || {};
                return (
                  <div key={idx} className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3" data-testid={`finding-${f.finding_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      <span className="text-[10px] font-mono text-emerald-300">{f.module_key}</span>
                      {typeof f.confidence === 'number' && (
                        <span className="text-[10px] text-muted-foreground">confidence {(f.confidence * 100).toFixed(0)}%</span>
                      )}
                      {typeof f.effect_size === 'number' && f.effect_size > 0 && (
                        <span className="text-[10px] text-muted-foreground">effect {(f.effect_size).toFixed(2)}</span>
                      )}
                    </div>
                    <p className="text-xs text-foreground/95 leading-relaxed">{f.narrative}</p>
                    <EvidenceChart spec={f.chart_spec} />
                  </div>
                );
              }
              if (e.event_type === 'error') {
                return (
                  <div key={idx} className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive flex items-start gap-2" data-testid="event-error">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{e.payload?.message || 'Investigation error'}</span>
                  </div>
                );
              }
              return null;
            })}

            {pendingClarifications && pendingClarifications.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="clarifications-panel">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Before we continue</span>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">The Planner would like to clarify a couple of things:</p>
                <div className="space-y-2">
                  {pendingClarifications.map((q, i) => (
                    <div key={i} data-testid={`clarification-${i}`}>
                      <p className="text-xs text-foreground mb-1">{q}</p>
                      <Textarea
                        value={clarifDraft[q] || ''}
                        onChange={(e) => setClarifDraft((prev) => ({ ...prev, [q]: e.target.value }))}
                        placeholder="Your answer…"
                        className="min-h-[48px] text-xs resize-none"
                        data-testid={`clarification-input-${i}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3">
                  <Button size="sm" onClick={submitClarifications} className="gap-1 text-[10px]" data-testid="clarifications-submit">
                    <Send className="w-3 h-3" /> Continue investigation
                  </Button>
                </div>
              </div>
            )}

            {verdict && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 order-first" data-testid="investigate-verdict">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">Verdict</span>
                </div>
                <p className="text-sm text-foreground/95 leading-relaxed whitespace-pre-line">{verdict.verdict}</p>
                {Array.isArray(verdict.top_drivers) && verdict.top_drivers.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid="verdict-drivers">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Top drivers</p>
                    {verdict.top_drivers.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <ArrowRight className="w-3 h-3 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-foreground/90">{d.statement}</span>
                          {d.category && <Badge className={`ml-2 text-[8px] border ${CATEGORY_TINT[d.category] || 'bg-muted text-muted-foreground'}`}>{d.category}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {Array.isArray(verdict.follow_ups) && verdict.follow_ups.length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="verdict-follow-ups">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Follow-ups</p>
                    {verdict.follow_ups.map((f, i) => (
                      <button
                        key={i}
                        onClick={() => setQuestion(f)}
                        className="block text-left text-[11px] text-primary hover:text-primary/80 hover:underline"
                        data-testid={`verdict-follow-up-${i}`}
                      >
                        · {f}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-emerald-500/20 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!investigation?.investigation_id) return;
                      try {
                        const d = await api.createDiscussion({
                          question: question.trim(),
                          data_context: {
                            investigation_id: investigation.investigation_id,
                            metric: target?.metric,
                            model_id: target?.model_id,
                          },
                          report_state: {
                            source: 'investigation',
                            verdict: verdict.verdict,
                            top_drivers: verdict.top_drivers || [],
                          },
                        });
                        toast.success('Discussion thread created');
                        onOpenChange(false);
                        navigate(`/discussions?id=${d.discussion_id}`);
                      } catch (err) { toast.error(err.message || 'Failed'); }
                    }}
                    className="h-7 text-[10px] gap-1.5"
                    data-testid="investigation-start-thread"
                  >
                    <MessageSquare className="w-3 h-3" /> Start thread on this
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!investigation?.investigation_id) return;
                      try {
                        const r = await api.createInvestigationShare(investigation.investigation_id);
                        const url = `${window.location.origin}${r.share_url}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success('Share link copied to clipboard');
                        } catch {
                          toast.success('Share link ready — see console');
                          console.log('INZARA snapshot URL:', url);
                        }
                      } catch (err) { toast.error(err.message || 'Failed to create snapshot'); }
                    }}
                    className="h-7 text-[10px] gap-1.5"
                    data-testid="investigation-share-snapshot"
                  >
                    <Share2 className="w-3 h-3" /> Share snapshot
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!investigation?.investigation_id) return;
                      try {
                        const fresh = await api.rerunInvestigation(investigation.investigation_id);
                        if (esRef.current) { esRef.current.close(); esRef.current = null; }
                        setEvents([]);
                        setVerdict(null);
                        setComments([]);
                        setInvestigation({ investigation_id: fresh.investigation_id, rerun_of: fresh.rerun_of });
                        setStatus('running');
                        setRunning(true);
                        startStream(fresh.investigation_id);
                        toast.success('Rerun started with fresh data');
                      } catch (err) { toast.error(err.message || 'Rerun failed'); }
                    }}
                    className="h-7 text-[10px] gap-1.5"
                    data-testid="investigation-rerun"
                  >
                    <RefreshCw className="w-3 h-3" /> Rerun
                  </Button>
                </div>
                {verdict.critique && (Array.isArray(verdict.critique.concerns) && verdict.critique.concerns.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-emerald-500/20" data-testid="verdict-critique">
                    <p className="text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3 h-3" /> Critic notes
                    </p>
                    <ul className="space-y-0.5">
                      {verdict.critique.concerns.map((c, i) => (
                        <li key={i} className="text-[11px] text-amber-200/80">· {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {status === 'failed' && !verdict && (
              <div className="text-center py-6 text-xs text-destructive">Investigation failed — see the timeline above.</div>
            )}
            {status === 'cancelled' && (
              <div className="text-center py-6 text-xs text-muted-foreground">Cancelled by user.</div>
            )}

            {/* Comments — shared workspace for the team */}
            {investigation?.investigation_id && (
              <div className="mt-4 rounded-xl border border-border/40 bg-card/30 p-3" data-testid="investigation-comments">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                  <MessageSquare className="w-3 h-3 text-muted-foreground" /> Comments
                  <span className="text-[10px] text-muted-foreground font-normal">({comments.length})</span>
                </div>
                <div className="space-y-2 mb-2 max-h-56 overflow-y-auto">
                  {comments.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">No comments yet. Start the conversation below.</p>
                  )}
                  {comments.map((c) => (
                    <div key={c.comment_id} className="text-[11px] p-2 rounded-md bg-muted/20 border border-border/30" data-testid={`comment-${c.comment_id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-0.5">
                        <span className="font-semibold text-foreground/90">{c.author_name}</span>
                        <span>·</span>
                        <span>{new Date(c.created_at).toLocaleString()}</span>
                        {c.anchor_type !== 'verdict' && (
                          <>
                            <span>·</span>
                            <span className="text-indigo-300 font-mono">
                              {c.anchor_type}:{(c.anchor_id || '').slice(0, 12)}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-foreground/95 whitespace-pre-wrap">{c.body}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5 items-start">
                  <select
                    className="text-[10px] h-7 rounded-md border border-border/50 bg-muted/20 px-1.5"
                    value={`${commentAnchor.type}:${commentAnchor.id}`}
                    onChange={(e) => {
                      const [type, id] = e.target.value.split(':');
                      setCommentAnchor({ type, id });
                    }}
                    data-testid="comment-anchor-select"
                  >
                    <option value="verdict:verdict">Verdict</option>
                    {hypotheses.map((h) => (
                      <option key={h.hypothesis_id} value={`hypothesis:${h.hypothesis_id}`}>H: {(h.statement || '').slice(0, 40)}</option>
                    ))}
                    {findings.map((f) => (
                      <option key={f.finding_id} value={`finding:${f.finding_id}`}>F: {(f.narrative || '').slice(0, 40)}</option>
                    ))}
                  </select>
                  <Input
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Add a comment…"
                    className="h-7 text-[11px] flex-1"
                    data-testid="comment-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && commentDraft.trim()) {
                        e.preventDefault();
                        submitComment();
                      }
                    }}
                  />
                  <Button size="sm" onClick={submitComment} disabled={!commentDraft.trim()} className="h-7 text-[10px]" data-testid="comment-submit">
                    Post
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {running && (
          <div className="px-4 py-2 border-t border-border/30 flex-shrink-0 flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="investigate-status-strip">
            <Loader2 className="w-3 h-3 animate-spin text-indigo-400" />
            <span>Investigation in progress · {hypotheses.length} hypothes{hypotheses.length === 1 ? 'is' : 'es'} generated</span>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default InvestigatePanel;
